import React, {useState} from 'react';
import {Check, X, Plus, Eye, ExternalLink} from 'lucide-react';
import {useAuth} from "../../../providers/AuthProvider";
import {useSupplierServiceOfferingFormItem} from "../providers/SupplierServiceOfferingFormItemProvider";
import FlipMove from "react-flip-move";
import {PictureOutlined} from "@ant-design/icons";
import InfoTooltip from "../../../components/InfoTooltip";
import {Divider, Empty} from "antd";
import SearchBrandsInput from "../../../components/form/SearchBrandsInput";
import {FormInputComponent} from "../../../components/form/Form";

const MAX_BRANDS_COUNT = 8;
const MAX_ASSETS_COUNT = 10;

const ServiceOfferingsSubStepRelatedWork = ({...props}) => {
    const {auth} = useAuth();
    const {
        profileItem,
        profileIndex,
        onModelChange,
        refresh,
        isEditing
    } = useSupplierServiceOfferingFormItem();
    const supplierOrg = auth?.supplierOrg();
    const {
        brands = [],
        assets = [],
        key_clients = [],
    } = supplierOrg;

    const selectedAssets = profileItem.assets || [];
    const selectedBrands = profileItem.brands || [];
    const selectedKeyClients = profileItem.key_clients || [];

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentAsset, setCurrentAsset] = useState(null);
    const [currentAssetIndex, setCurrentAssetIndex] = useState(0);
    const [filterCategory, setFilterCategory] = useState('All');

    const checkFileType = (file) => {
        const isImageMime = file.type && file.type.startsWith('image/')
        const isVideoMime = file.type && file.type.startsWith('video/')
        const isImageExt = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.path)
        const isVideoExt = /\.(mp4|webm|mov|avi|wmv)$/i.test(file.path)

        return {
            isImage: isImageMime || isImageExt || file.content_type === 'Image',
            isVideo: isVideoMime || isVideoExt || file.content_type === 'Video'
        }
    }
    // Get unique categories
    const categories = ['All', ...new Set(assets.map(asset => asset.content_type))];

    // Filter assets by category
    const filteredAssets = filterCategory === 'All'
        ? assets
        : assets.filter(asset => asset.content_type === filterCategory);

    const toggleAssetSelection = (asset) => {
        let prev = TB.cloneDeep(selectedAssets)
        const isSelected = prev.some(selected => selected.id === asset.id);
        if (isSelected) {
            prev = prev.filter(selected => selected.id !== asset.id);
        } else {
            prev = [...prev, asset];
        }
        onModelChange({assets: prev});
    };

    const openLightbox = (asset, index) => {
        setCurrentAsset(asset);
        setCurrentAssetIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setCurrentAsset(null);
    };

    const navigateLightbox = (direction) => {
        const newIndex = direction === 'next'
            ? (currentAssetIndex + 1) % filteredAssets.length
            : (currentAssetIndex - 1 + filteredAssets.length) % filteredAssets.length;

        setCurrentAssetIndex(newIndex);
        setCurrentAsset(filteredAssets[newIndex]);
    };

    const isAssetSelected = (asset) => selectedAssets.some(selected => selected.id === asset.id);
    const isKeyClientSelected = (keyClient) => selectedKeyClients.some(selected => selected.id === keyClient.id);


    return (
        <div className="flex flex-col space-y-6">
            {/* Brands Section */}
            <div className="space-y-4">
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Add Brands</h1>
                    <p className="text-gray-600">Select related brands who have used this service</p>
                </div>
                <div className="field px-3.5">
                    <label className="col-form-label flex flex-row items-center gap-7">
                        {selectedBrands.length > 0 && (
                            <span className={TB.mapClass("ml-auto text-xxs italic", {
                                "opacity-7": selectedBrands.length < MAX_BRANDS_COUNT,
                                "text-danger font-semibold opacity-1": selectedBrands.length === MAX_BRANDS_COUNT,
                            })}>
                                Max ({selectedBrands.length} / {MAX_BRANDS_COUNT})
                            </span>
                        )}
                    </label>

                    {brands.length > 0 && (
                        <div className="items-stretch flex flex-col mt-5">
                            <div className="discipline-skills-container">
                                <FlipMove typeName={null} maintainContainerHeight={true}>
                                    {brands.map((brand, brandIndex) => {
                                        const brandKey = TB.slugify(`brand-${profileIndex}-${(brand.id || brand.domain || brand.name)}`);
                                        let isBrandSelected = TB.find(selectedBrands, ['id', brand.id])
                                        return (
                                            <div key={brandKey}
                                                 className="group bg-gray-50 ml-8 mb-8 self-stretch flex flex-row items-center p-1 relative">
                                                <div id={brandKey}>
                                                    <div className={TB.mapClass("w-24 self-center", {
                                                        'opacity-50 group-hover:opacity-100': !isBrandSelected,
                                                        'opacity-100': isBrandSelected
                                                    })}>
                                                        {(brand.logo || brand.icon) ?
                                                            <img className="w-full max-w-full"
                                                                 src={brand.logo || brand.icon} alt={brand.name}/> :
                                                            <PictureOutlined className="w-full max-w-full"
                                                                             style={{fontSize: 56}}/>
                                                        }
                                                    </div>
                                                </div>
                                                <InfoTooltip
                                                    tooltipProps={{className: '[&_.tooltip-inner]:max-w-[300px] [&_.tooltip-inner]:min-w-[200px]'}}
                                                    type="white" target={brandKey} placement="top">
                                                    <div className="flex flex-col gap-1">
                                                        <p className="p-0 text-left m-0 text-lg">{brand.name || brand.domain}</p>
                                                        {brand.name && brand.domain && (
                                                            <p className="p-0 text-left m-0 text-sm text-info whitespace-nowrap">
                                                                {brand.domain}
                                                            </p>
                                                        )}
                                                    </div>
                                                </InfoTooltip>
                                                <div className="absolute point-events-auto top-0 right-0">
                                                    {(isBrandSelected || (selectedBrands?.length < MAX_BRANDS_COUNT)) && (
                                                        <button
                                                            onClick={() => {
                                                                const updatedBrands = TB.xorBy([...selectedBrands], [brand], 'id');

                                                                console.log('brands', updatedBrands)
                                                                onModelChange({
                                                                    ...profileItem,
                                                                    brands: updatedBrands
                                                                });
                                                            }}
                                                            className={`w-5 h-5 rounded-full pointer-events-auto flex items-center justify-center transition-all ${
                                                                isBrandSelected
                                                                    ? 'bg-blue-600 text-white'
                                                                    : 'bg-white bg-opacity-90 text-gray-700 hover:bg-opacity-100'
                                                            }`}
                                                        >
                                                            {isBrandSelected ? <Check className="w-3 h-3"/> :
                                                                <Plus className="w-3 h-3"/>}
                                                        </button>
                                                    )}
                                                </div>

                                            </div>
                                        );
                                    })}
                                </FlipMove>
                            </div>
                            <Divider className="mt-0 mb-4"/>
                        </div>
                    )}

                    {isEditing && (
                        <div className="mt-1">
                            <SearchBrandsInput
                                isDisabled={brands.length >= MAX_BRANDS_COUNT}
                                placeholder={brands.length < MAX_BRANDS_COUNT ? "Search for brands" : "Max brands limit reached"}
                                autoClearSearchValue={true}
                                shouldReset={true}
                                value={null}
                                disabledOptions={TB.map(brands, brand => brand.id || brand.brandId || brand.domain)}
                                onChange={(brand) => onModelChange({
                                    ...profileItem,
                                    brands: [...brands, brand]
                                })}
                            />
                        </div>
                    )}

                    {/* Hidden form field for brands */}
                    <FormInputComponent
                        type="hidden"
                        style={{display: 'none'}}
                        fieldName="brands"
                        value={profileItem.brands}
                    />
                </div>
            </div>
            {/* Work Gallery Section */}
            <div className="space-y-4">
                <div className="field px-3.5">
                    <div className="items-stretch flex flex-col mt-5">
                        <div>
                            <div className="max-w-7xl mx-auto">
                                <div className="mb-8">
                                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Add Work Gallery Assets</h1>
                                    <p className="text-gray-600">Select from your portfolio pieces to include in your
                                        service offering</p>
                                </div>

                                {assets?.length > 0 ? (
                                    <React.Fragment>
                                        {/* Category Filter */}
                                        <div className="mb-6 flex flex-wrap gap-2">
                                            {categories.map(category => (
                                                <button
                                                    key={category}
                                                    onClick={() => setFilterCategory(category)}
                                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                                        filterCategory === category
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                                    }`}
                                                >
                                                    {category}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Selected Assets Counter & Add Button */}
                                        <div
                                            className="mb-6 flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
                                            <div className="flex items-center gap-4">
                                                <span className="text-sm text-gray-600">
                                                    {selectedAssets.length} asset(s) selected
                                                </span>
                                                {selectedAssets.length > 0 && (
                                                    <div className="flex -space-x-2">
                                                        {selectedAssets.slice(0, 8).map(asset => checkFileType(asset)?.isImage ? (
                                                            <img
                                                                key={asset.id}
                                                                src={asset.path}
                                                                alt={asset.content_type}
                                                                className="w-10 h-10 rounded-full border-2 border-white object-cover"
                                                            />
                                                        ) : (
                                                            <div
                                                                className="w-10 h-10 bg-transparent flex flex-col items-center justify-center">
                                                                <video
                                                                    key={asset.id}
                                                                    muted
                                                                    controls={false}
                                                                    src={asset.path}
                                                                    className="max-w-10 w-auto h-10 rounded-full border-2 border-white object-cover"
                                                                />
                                                            </div>
                                                        ))}
                                                        {selectedAssets.length > 8 && (
                                                            <div
                                                                className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                                                                +{selectedAssets.length - 8}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {/*<button*/}
                                            {/*    onClick={addToServiceOffering}*/}
                                            {/*    disabled={selectedAssets.length === 0}*/}
                                            {/*    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${*/}
                                            {/*        selectedAssets.length > 0*/}
                                            {/*            ? 'bg-green-600 text-white hover:bg-green-700'*/}
                                            {/*            : 'bg-gray-300 text-gray-500 cursor-not-allowed'*/}
                                            {/*    }`}*/}
                                            {/*>*/}
                                            {/*    <Plus className="w-4 h-4" />*/}
                                            {/*    Add to Service Offering*/}
                                            {/*</button>*/}
                                        </div>

                                        {/* Asset Grid */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                                            {filteredAssets.map((asset, index) => {

                                                return (
                                                    <div key={asset.id}
                                                         className="group relative bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                                                        <div className={TB.mapClass("relative aspect-[4/3] overflow-hidden")}>
                                                            {checkFileType(asset)?.isImage ? (
                                                                <img
                                                                    src={asset.path}
                                                                    alt={asset.content_type}
                                                                    className={TB.mapClass("w-full h-full object-cover pointer-events-none transition-transform group-hover:scale-105", {
                                                                        "opacity-60": !isAssetSelected(asset),
                                                                    })}
                                                                />
                                                            ) : (
                                                                <video
                                                                    className={TB.mapClass("w-full h-full object-cover pointer-events-none transition-transform group-hover:scale-105", {
                                                                        "opacity-60": !isAssetSelected(asset),
                                                                    })}
                                                                    controls={false} muted src={asset.path}/>
                                                            )}

                                                            {/* Selection Overlay */}
                                                            <div
                                                                className={`absolute inset-0 pointer-events-auto transition-opacity ${
                                                                    isAssetSelected(asset) ? 'bg-blue-600 bg-opacity-20 opacity-100' : 'bg-black bg-opacity-10 group-hover:bg-opacity-20 opacity-80 group-hover:opacity-100'
                                                                }`}>
                                                                <div className="absolute top-2 right-2 flex gap-2">
                                                                    <button
                                                                        onClick={() => openLightbox(asset, index)}
                                                                        className="w-8 h-8 bg-white bg-opacity-90 pointer-events-auto rounded-full flex items-center justify-center text-gray-700 hover:bg-opacity-100 transition-all"
                                                                    >
                                                                        <Eye className="w-4 h-4"/>
                                                                    </button>
                                                                    {(isAssetSelected(asset) || (selectedAssets?.length < MAX_ASSETS_COUNT)) && (
                                                                        <button
                                                                            onClick={() => toggleAssetSelection(asset)}
                                                                            className={`w-8 h-8 rounded-full pointer-events-auto flex items-center justify-center transition-all ${
                                                                                isAssetSelected(asset)
                                                                                    ? 'bg-blue-600 text-white'
                                                                                    : 'bg-white bg-opacity-90 text-gray-700 hover:bg-opacity-100'
                                                                            }`}
                                                                        >
                                                                            {isAssetSelected(asset) ?
                                                                                <Check className="w-4 h-4"/> :
                                                                                <Plus className="w-4 h-4"/>}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="p-4">
                                                            <p className="text-xxs text-gray-600 line-clamp-2">{asset.caption}</p>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </React.Fragment>
                                ) : (
                                    <Empty description="No work gallery assets available to add."
                                           image={Empty.PRESENTED_IMAGE_SIMPLE}/>
                                )}


                                {/* Lightbox */}
                                {lightboxOpen && currentAsset && (
                                    <div
                                        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
                                        <div className="relative max-w-4xl max-h-full">
                                            <button
                                                onClick={closeLightbox}
                                                className="absolute top-4 right-4 w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-all z-10"
                                            >
                                                <X className="w-6 h-6"/>
                                            </button>

                                            {/* Navigation arrows */}
                                            <button
                                                onClick={() => navigateLightbox('prev')}
                                                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-all"
                                            >
                                                ←
                                            </button>
                                            <button
                                                onClick={() => navigateLightbox('next')}
                                                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-all"
                                            >
                                                →
                                            </button>

                                            <div className="text-center">
                                                {checkFileType(currentAsset)?.isImage ? (
                                                    <img
                                                        src={currentAsset.path}
                                                        alt={currentAsset.content_type}
                                                        className="max-w-full max-h-[70vh] object-contain mx-auto rounded-lg"
                                                    />
                                                ) : (
                                                    <video
                                                        autoPlay
                                                        controls={true}
                                                        src={currentAsset.path}
                                                        className="max-w-full max-h-[70vh] object-contain mx-auto rounded-lg"
                                                    />
                                                )}
                                                <div className="mt-4 text-white">
                                                    <p className="text-gray-300 mb-2">{currentAsset.content_type}</p>
                                                    <p className="text-gray-400">{currentAsset.caption || ''}</p>
                                                    <div className="mt-4 flex justify-center gap-4">
                                                        <button
                                                            onClick={() => toggleAssetSelection(currentAsset)}
                                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                                                                isAssetSelected(currentAsset)
                                                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                                    : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                                                            }`}
                                                        >
                                                            {isAssetSelected(currentAsset) ?
                                                                <Check className="w-4 h-4"/> :
                                                                <Plus className="w-4 h-4"/>}
                                                            {isAssetSelected(currentAsset) ? 'Selected' : 'Select'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <Divider className="mt-0 mb-4"/>
                    </div>

                    {/* Hidden form field for brands */}
                    <FormInputComponent
                        type="hidden"
                        style={{display: 'none'}}
                        fieldName="assets"
                        value={profileItem.assets}
                    />
                </div>
            </div>

            {/* Project Highlights Section */}
            <div className="space-y-4">
                <div className="field px-3.5">
                    <div className="items-stretch flex flex-col mt-5">
                        <div>
                            <div className="max-w-7xl mx-auto">
                                <div className="mb-8">
                                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Add Project Highlights</h1>
                                    <p className="text-gray-600">Select from your project highlights to include in your
                                        service offering</p>
                                </div>


                                {/* Selected Project Highlights */}
                                <div
                                    className="mb-6 flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm text-gray-600">
                                            {selectedKeyClients.length} projects(s) selected
                                        </span>
                                        {selectedKeyClients.length > 0 && (
                                            <div className="flex -space-x-2">
                                                {selectedKeyClients.slice(0, 8).map(key_client => key_client.image_path && (
                                                    <img
                                                        key={key_client.id}
                                                        src={key_client.image_path}

                                                        className="w-10 h-10 rounded-full border-2 border-white object-cover"
                                                    />
                                                ))}
                                                {selectedKeyClients.length > 8 && (
                                                    <div
                                                        className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                                                        +{selectedKeyClients.length - 8}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {/*<button*/}
                                    {/*    onClick={addToServiceOffering}*/}
                                    {/*    disabled={selectedAssets.length === 0}*/}
                                    {/*    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${*/}
                                    {/*        selectedAssets.length > 0*/}
                                    {/*            ? 'bg-green-600 text-white hover:bg-green-700'*/}
                                    {/*            : 'bg-gray-300 text-gray-500 cursor-not-allowed'*/}
                                    {/*    }`}*/}
                                    {/*>*/}
                                    {/*    <Plus className="w-4 h-4" />*/}
                                    {/*    Add to Service Offering*/}
                                    {/*</button>*/}
                                </div>

                                {/* Asset Grid */}
                                <div className="flex flex-col gap-6">
                                    {key_clients?.length > 0 ? key_clients.map((keyClient, index) => {

                                        return (
                                            <div key={keyClient.id}
                                                 className={TB.mapClass("group relative rounded-lg hover:shadow-md transition-shadow overflow-hidden flex flex-row gap-2 ", {
                                                     "shadow-sm bg-white ": !isKeyClientSelected(keyClient),
                                                     "shadow-md bg-slate-50 border border-blue-100": isKeyClientSelected(keyClient),
                                                 })}>
                                                {keyClient.image_path && (
                                                    <div
                                                        className={TB.mapClass("relative aspect-[4/3] w-40 overflow-hidden")}>
                                                        <img
                                                            src={keyClient.image_path}
                                                            className={TB.mapClass("w-full h-full object-cover pointer-events-none transition-transform group-hover:scale-105", {
                                                                "opacity-60": !isKeyClientSelected(keyClient),
                                                                "scale-105": isKeyClientSelected(keyClient),
                                                            })}
                                                        />
                                                    </div>
                                                )}

                                                <div className="flex flex-col items-start flex-grow gap-2 p-4">
                                                    <p className={TB.mapClass("text-base font-semibold line-clamp-2", {
                                                        "text-gray-600 group-hover:text-black": !isKeyClientSelected(keyClient),
                                                        "text-black": isKeyClientSelected(keyClient)
                                                    })}>{keyClient.headline}</p>
                                                    <p className={TB.mapClass("text-xxs overflow-ellipsis line-clamp-5 font-semibold", {
                                                        "text-gray-600 group-hover:text-black": !isKeyClientSelected(keyClient),
                                                        "text-black": isKeyClientSelected(keyClient)
                                                    })}
                                                       dangerouslySetInnerHTML={{__html: keyClient.description || '<i>No description provided...<i/>'}}/>

                                                    <button onClick={() => {
                                                        let key_clients = TB.xorBy([...selectedKeyClients], [keyClient], 'id');
                                                        return onModelChange({
                                                            ...profileItem,
                                                            key_clients
                                                        })
                                                    }} className={TB.mapClass("btn-link self-end", {
                                                        "btn-link-danger": isKeyClientSelected(keyClient),
                                                        "btn-link-primary": !isKeyClientSelected(keyClient),
                                                    })}>
                                                        {isKeyClientSelected(keyClient) ? 'Remove from service offering' : 'Add to service offering'}
                                                    </button>
                                                </div>

                                            </div>
                                        )
                                    }) :  (
                                        <Empty description="No Project Highlights available to add."
                                               image={Empty.PRESENTED_IMAGE_SIMPLE}/>
                                    )}
                                </div>

                            </div>
                        </div>
                        <Divider className="mt-0 mb-4"/>
                    </div>

                    {/* Hidden form field for key_clients */}
                    <FormInputComponent
                        type="hidden"
                        style={{display: 'none'}}
                        fieldName="assets"
                        value={profileItem.key_clients}
                    />
                </div>
            </div>

        </div>
    );
};
export default ServiceOfferingsSubStepRelatedWork